import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { apiError, rupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const empty = {
  name: "",
  start_time: "07:00",
  end_time: "16:00",
  shift_wage: 0,
  auto_close_minutes: "",
};

export default function MasterShiftTab() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setShifts((await api.get("/shifts")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, start_time: s.start_time, end_time: s.end_time, shift_wage: s.shift_wage || 0, auto_close_minutes: s.auto_close_minutes ?? "" }); setEditing(s); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, shift_wage: Number(form.shift_wage), auto_close_minutes: form.auto_close_minutes === "" ? null : Number(form.auto_close_minutes) };
      if (editing) await api.put(`/shifts/${editing.id}`, payload);
      else await api.post("/shifts", payload);
      toast.success("Shift disimpan");
      setOpen(false); await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus shift "${s.name}"?`)) return;
    try { await api.delete(`/shifts/${s.id}`); toast.success("Shift dihapus"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Master Shift</h2>
        <Button data-testid="add-shift-button" onClick={openAdd} className="bg-[#3368A0] hover:bg-[#284f7a] rounded-xl">
          <Plus className="h-4 w-4 mr-1.5" /> Tambah Shift
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm" data-testid="shift-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-5 py-3">Nama Shift</th>
              <th className="text-left px-5 py-3">Jam Masuk</th>
              <th className="text-left px-5 py-3">Jam Pulang</th>
              <th className="text-right px-5 py-3">Upah Tambahan</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-5 py-3 font-medium text-slate-700">{s.name}</td>
                <td className="px-5 py-3 font-mono text-slate-600">{s.start_time}</td>
                <td className="px-5 py-3 font-mono text-slate-600">{s.end_time}</td>
                <td className="px-5 py-3 text-right font-mono text-slate-600">{s.shift_wage ? rupiah(s.shift_wage) : "-"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => openEdit(s)} data-testid={`edit-shift-${s.id}`} className="text-[#3368A0] hover:text-sky-800 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(s)} data-testid={`delete-shift-${s.id}`} className="text-rose-500 hover:text-rose-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editing ? "Edit Shift" : "Tambah Shift"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nama Shift</Label>
              <Input data-testid="shift-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Shift 1" />
            </div>
            <div className="space-y-1.5">
              <Label>Auto-Close Check-Out (menit, opsional)</Label>
              <Input type="number" min="0" data-testid="shift-auto-close-input" value={form.auto_close_minutes}
                onChange={(e) => setForm({ ...form, auto_close_minutes: e.target.value })} />
              <p className="text-xs text-slate-400">Prioritas pertama sebelum pengaturan outlet dan default perusahaan.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Jam Masuk</Label>
                <Input type="time" data-testid="shift-start-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Jam Pulang</Label>
                <Input type="time" data-testid="shift-end-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Upah Tambahan Shift (Rp)</Label>
              <Input type="number" data-testid="shift-wage-input" value={form.shift_wage}
                onChange={(e) => setForm({ ...form, shift_wage: e.target.value })} />
              <p className="text-xs text-slate-400">Bonus per hari kerja di shift ini, ditambahkan ke gaji harian. Contoh Long Shift +Rp 35.000.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="save-shift-button" onClick={save} disabled={saving} className="bg-[#3368A0] hover:bg-[#284f7a]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
